import React, { memo, useContext, useMemo, useEffect } from "react";
// components
import { PaymentAlert } from "@swiggy-private/payment-ui";
// types
import { WebviewRefreshOrderIdMismatchAlertPayload } from "@swiggy-private/checkout-core";
import {
    CheckoutContext,
    CheckoutActionTypes,
    recordNREvent,
    NEWRELIC_TABLES,
    AnalyticsContext,
} from "@swiggy-private/checkout-core";
// analytics
import {
    impressionWebviewRefreshMismatchAlertEvent,
    clickWebviewRefreshMismatchPrimaryCTAEvent,
} from "@payment-client/analytics/events/WebviewRefreshOrderIsMismatch";
// helpers
import { isEmpty } from "@swiggy-private/core-ui";
// constants
import { ORDERID_MISMATCH_ALERT_DATA } from "@payment-client/constants/WebviewRefresh";

interface WebviewRefreshOrderIdMismatchAlertProps {
    payload?: WebviewRefreshOrderIdMismatchAlertPayload;
}

function WebviewRefreshOrderIdMismatchAlert({
    payload,
}: WebviewRefreshOrderIdMismatchAlertProps): React.ReactElement | null {
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const { checkoutState } = useContext(CheckoutContext);

    const title = payload?.alertData?.title || ORDERID_MISMATCH_ALERT_DATA.title;
    const icon = payload?.alertData?.icon || ORDERID_MISMATCH_ALERT_DATA.icon;

    const primaryCTAData = useMemo(() => {
        return {
            text: payload?.alertData?.cta?.text || ORDERID_MISMATCH_ALERT_DATA.ctaText,
            onClick: () => {
                try {
                    clickWebviewRefreshMismatchPrimaryCTAEvent(clickEvent);
                    payload?.alertData?.cta?.onClick?.();
                } catch {
                    // no-op
                }
            },
        };
    }, [clickEvent, payload?.alertData?.cta]);

    useEffect(() => {
        if (checkoutState.type === CheckoutActionTypes.WEBVIEW_REFRESH_ORDER_ID_MISMATCH_ALERT) {
            impressionWebviewRefreshMismatchAlertEvent(impressionEvent);
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_INFO,
                name: "webviewRefreshOrderIdClashHalfcardShown",
            });
        }
    }, [checkoutState.type, impressionEvent]);

    if (
        checkoutState.type !== CheckoutActionTypes.WEBVIEW_REFRESH_ORDER_ID_MISMATCH_ALERT ||
        isEmpty(payload?.alertData)
    ) {
        return null;
    }

    return (
        <PaymentAlert
            title={title}
            primaryAction={primaryCTAData}
            hideOnOverlayClick={false}
            icon={icon}
            hideCloseIcon={true}
        />
    );
}

export default memo(WebviewRefreshOrderIdMismatchAlert);
